import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from "@microsoft/signalr";
import type {
  IWebSocketClient,
  IWebSocketHandler,
  IWebSocketOptions,
} from "./socket";

/**
 * SignalR을 사용한 WebSocket 클라이언트 구현체
 * 메시지 큐잉 및 자동 재연결 기능 포함
 */
export class SlotWebSocketClient implements IWebSocketClient {
  private connection: HubConnection | null = null;
  private handler: IWebSocketHandler | null = null;
  private isOnCloseMessage: boolean = true;
  private messageQueue: { command: string; data: string }[] = [];

  /**
   * WebSocket 서버에 연결
   * @param options 연결 옵션 (URL, 이벤트 핸들러)
   */
  public connect(options: IWebSocketOptions): void {
    if (this.connection) {
      this.handler?.onSocketError?.(new Error("Connection is already open"));
      return;
    }

    const { url, handler } = options;
    this.handler = handler;
    this.connection = new HubConnectionBuilder()
      .withUrl(url)
      .withServerTimeout(60000) // 60초 - 서버 타임아웃
      .withKeepAliveInterval(60000) // 1분 - 자체 PING/PONG 사용하므로 길게 설정
      .build();

    this.setupEventHandlers();
    this.startConnection();
  }

  /**
   * 이벤트 핸들러 설정
   */
  private setupEventHandlers(): void {
    if (!this.connection || !this.handler) return;

    this.connection.on("ServerMessage", (type: string, payload: any) => {
      this.handler?.onResponse?.({ type, payload });
    });

    this.connection.onclose((error: Error | undefined) => {
      this.handleConnectionClose(error);
    });
  }

  /**
   * 연결 시작
   */
  private startConnection(): void {
    this.connection
      ?.start()
      .then(() => {
        this.handler?.onConnected?.();
        this.processMessageQueue(); // 재연결 성공 시 큐에 쌓인 메시지 전송
      })
      .catch((error: Error) => {
        this.handler?.onSocketError?.(error);
        this.connection = null;
      });
  }

  /**
   * 메시지 큐 처리
   */
  private processMessageQueue(): void {
    this.messageQueue.forEach((message) => {
      this.connection?.send("ClientMessage", message.command, message.data);
    });
    this.messageQueue = [];
  }

  /**
   * 연결 종료 처리
   */
  private handleConnectionClose(error: Error | undefined): void {
    this.connection = null;

    if (this.isOnCloseMessage) {
      this.handler?.onSocketClose?.(
        new CloseEvent("close", {
          wasClean: true,
          code: 1000,
          reason: error?.message || "",
        })
      );
    }

    this.isOnCloseMessage = true;
  }

  /**
   * 메시지 전송
   * @param command 명령어
   * @param data 전송할 데이터
   */
  public send(command: string, data: string): void {
    if (!this.connection) {
      this.handler?.onSocketError?.(new Error("Connection is not open"));
      return;
    }

    if (this.connection.state === HubConnectionState.Connected) {
      this.connection.send("ClientMessage", command, data);
    } else {
      this.messageQueue.push({ command, data });
    }
  }

  /**
   * 연결 종료
   */
  public close(): void {
    this.isOnCloseMessage = false;
    this.connection?.stop();
    this.connection = null;
    this.handler?.onSocketClose?.(
      new CloseEvent("close", {
        wasClean: true,
        code: 1000,
        reason: "WebSocket closed by user",
      })
    );
  }

  /**
   * PING 메시지 전송
   */
  public ping(): void {
    if (this.connection?.state === HubConnectionState.Connected) {
      this.send("PING", "");
    }
  }

  /**
   * 연결 상태 확인
   * @returns 연결 상태 (true: 연결됨, false: 연결되지 않음)
   */
  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }
}
